import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Card,
  CardBody,
  CardHeader,
  Col,
  Spinner,
  Input,
  InputGroup,
  Button,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { toast } from "react-toastify";
import { APIClient } from "../../../helpers/api_helper";
import AwesomePagination from "../../../Components/Common/AwesomePagination";
import moment from "moment";
import FeatherIcon from "feather-icons-react";
import ModalQrCode from "./ModalQrCode";

const apiClient = new APIClient();

const PixClientes = ({ isCliente }) => {
  const [loading, setLoading] = useState(false);
  const [shouldFetch, setShouldFetch] = useState(true);
  const [termo, setTermo] = useState("");
  const [inputTermo, setInputTermo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [orderList, setOrderList] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [modalQrOpen, setModalQrOpen] = useState(false);
  const [qrSelecionado, setQrSelecionado] = useState({ qrcode: "", valor: 0 });

  const user = JSON.parse(localStorage.getItem("authUser"));

  function formatName(name) {
    if (!name) return "";
    let lowerCaseName = name.toLowerCase();
    let properName = lowerCaseName.replace(
      /\b\w/g,
      function (letter, index, fullText) {
        const previousLetter = fullText[index - 1];
        const isWordAfterApostrophe = previousLetter === "'";
        if (isWordAfterApostrophe) {
          return letter;
        }
        const word = fullText.slice(index).match(/^\w+/)[0];
        const lowerCaseWords = ["de", "da", "do", "dos", "das"];
        if (lowerCaseWords.includes(word)) {
          return letter;
        }
        return letter.toUpperCase();
      }
    );

    // Converte a letra após um apóstrofo para maiúscula
    properName = properName.replace(/'\w/g, function (letter) {
      return letter.toUpperCase();
    });

    return properName;
  }

  const formatCurrency = (value) => {
    if (!value) {
      return "";
    }
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  const marcarPago = useCallback(
    async (ordem_id) => {
      setLoadingButton(true);
      const response = await apiClient.put(`/marcar-pago/${ordem_id}`);
      setLoadingButton(false);

      if (response.sucesso) {
        toast.success(response.mensagem);
        setShouldFetch(true);
      } else {
        toast.error(response.mensagem);
      }
    },
    []
  );

  const getClientes = useCallback(async () => {
    setLoading(true);
    const response = await apiClient.post(`/clientes/pix/buscar`, {
      user_id: isCliente ? user.data._id : 0,
      termo: termo,
      pagina: pagina,
      itens_pagina: 20,
    });

    if (response.sucesso) {
      setOrderList(response.dados.data);
      setTotalPaginas(response.dados.last_page);
      setTotalItens(response.dados.total);
    } else {
      toast.error(response.mensagem);
    }
    setLoading(false);
  }, [isCliente, user.data._id, pagina, termo]);

  useEffect(() => {
    if (shouldFetch) {
      const fetchData = async () => {
        await getClientes();
        setShouldFetch(false);
      };
      fetchData();
    }
  }, [shouldFetch, getClientes]);

  document.title = "Depósitos PIX | Painel Fort Exchange";

  const handleTermoKeyDown = (e) => {
    if (e.key === "Enter") {
      setTermo(inputTermo);
      setPagina(1);
      setShouldFetch(true);
    }
  };

  const renderStatusBadge = (status_id) => {
    const statusInt = parseInt(status_id);
    if (statusInt === 0) {
      return (
        <span className="badge badge-soft-danger text-uppercase">
          Falha
        </span>
      );
    } else if (statusInt === 1) {
      return (
        <span className="badge badge-soft-success text-uppercase">
          Completado
        </span>
      );
    } else if (statusInt === -1) {
      return (
        <span className="badge badge-soft-danger text-uppercase">
          Inválido
        </span>
      );
    } else {
      return (
        <span className="badge badge-soft-warning text-uppercase">
          Aguardando
        </span>
      );
    }
  };

  const renderActions = (item) => {
    const isAdmin = user?.data?.role === "Admin";
    const statusInt = parseInt(item.status_id);
    const daysDiff = moment().diff(item.created_at, 'days');

    const abrirModalQr = (item) => {
      setQrSelecionado({ qrcode: item.qrcode, valor: item.amount });
      setModalQrOpen(true);
    };

    if (statusInt > 1) {
      return (
        <div>
          <Button
            id={`viewQrTooltip${item.order_id}`}
            color="success"
            onClick={() => abrirModalQr(item)}
            className="btn-sm"
          >
            <FeatherIcon
              icon="eye"
              style={{ color: "white", height: "10px" }}
            />
            Ver QR{" "}
          </Button>
          {isAdmin && (
            <Button
              color="info"
              onClick={() => marcarPago(item.order_id)}
              className="btn-sm"
              title="Será creditado o valor do depósito na conta do cliente"
              style={{ marginLeft: "5px" }}
              disabled={loadingButton}
            >
              Marcar Pago
            </Button>
          )}
        </div>
      );
    } else if (daysDiff < 7 && statusInt !== 1 && isAdmin) {
      return (
        <Button
          color="info"
          onClick={() => marcarPago(item.order_id)}
          className="btn-sm"
          title="Será creditado o valor do depósito na conta do cliente"
          disabled={loadingButton}
        >
          Marcar Pago
        </Button>
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title="PIX"
            pageTitle={isCliente ? "Clientes Fort" : "Aplicações"}
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center border-0">
                  <h5 className="card-title mb-0 flex-grow-1">Depósitos PIX</h5>
                </CardHeader>
                <CardBody>
                  {/* Campo de busca */}
                  <div className="mb-3">
                    <InputGroup>
                      <div className="input-group-text">
                        <FeatherIcon icon="search" className="icon-dual" />
                      </div>
                      <Input
                        type="text"
                        placeholder="Buscar por cliente, order ID..."
                        value={inputTermo}
                        onChange={e => setInputTermo(e.target.value)}
                        onKeyDown={handleTermoKeyDown}
                      />
                    </InputGroup>
                  </div>
                  
                  {loading ? (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Spinner height={100} width={100} />
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th>Data</th>
                              <th>Cliente</th>
                              <th>Order ID</th>
                              <th>Valor</th>
                              <th>Status</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderList.length === 0 ? (
                              <tr>
                                <td colSpan={6} className="text-center text-muted py-4">
                                  Nenhum depósito encontrado.
                                </td>
                              </tr>
                            ) : (
                              orderList.map((item, idx) => (
                                <tr key={item.order_id || idx}>
                                  {/* Data */}
                                  <td>
                                    <b>{moment(item.created_at).format("DD/MM/YYYY")} </b>
                                    <small className="text-muted">{moment(item.created_at).format("HH:mm")}</small>
                                  </td>
                                  {/* Cliente */}
                                  <td>
                                    <div className="d-flex align-items-center">
                                      <Link to="#" className="currency_name flex-grow-1 ms-2 text-body">
                                        {formatName(item.user?.name)}
                                      </Link>
                                    </div>
                                  </td>
                                  {/* Order ID */}
                                  <td>
                                    <b style={{ color: "darkBlue" }}>{item.order_id}</b>
                                  </td>
                                  {/* Valor */}
                                  <td>
                                    {formatCurrency(item.amount)}
                                  </td>
                                  {/* Status */}
                                  <td>
                                    {renderStatusBadge(item.status_id)}
                                  </td>
                                  {/* Ações */}
                                  <td>
                                    {renderActions(item)}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                      <AwesomePagination
                        pagina={pagina}
                        totalPaginas={totalPaginas}
                        setPagina={p => {
                          setPagina(p);
                          setShouldFetch(true);
                        }}
                      />
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <ModalQrCode
        isOpen={modalQrOpen}
        toggle={() => setModalQrOpen(false)}
        qrData={qrSelecionado.qrcode}
        valor={qrSelecionado.valor}
      />
    </React.Fragment>
  );
};

export default PixClientes;
